import { memo, useCallback, useMemo } from "react";
import { Icon } from "@/components/icons/Icon";
import { cn } from "@/lib/styles.utils";
import { isMobile } from "@/lib/dimmensions.utils";
import {
  buildPaginationItems,
  PAGINATION_ELLIPSIS,
  type PaginationItem,
} from "./pagination.helper";
import { Limit } from "./Limit";

export interface PaginationProps {
  lastPage: number;
  currentPage: number;
  changePage: (pageIndex: number) => void;
  limit?: string;
  setLimit?: (newPage: string) => void;
}

const BASE_BUTTON_CLASS =
  "size-10  md:size-8 rounded-[2px] border flex items-center justify-center";

const NAV_BUTTON_CLASS =
  "bg-ib-white text-ib-black border-ib-gray-borders disabled:bg-ib-gray-borders disabled:text-ib-gray-disabled";

const PAGE_BUTTON_CLASS =
  "bg-ib-white text-ib-gray-darkest border-ib-gray-borders text-[12px] leading-[20px] disabled:text-ib-white disabled:font-[IberiaTextBold] disabled:font-ib-bold disabled:bg-ib-red-main disabled:border-ib-red-main";

interface NavButtonProps {
  testId: string;
  icon:
    | "doubleChevronLeft"
    | "chevronLeft"
    | "chevronRight"
    | "doubleChevronRight";
  ariaLabel: string;
  disabled: boolean;
  onClick: () => void;
}

const NavButton = ({
  testId,
  icon,
  ariaLabel,
  disabled,
  onClick,
}: NavButtonProps) => {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={ariaLabel}
      className={cn(BASE_BUTTON_CLASS, NAV_BUTTON_CLASS)}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={icon} className="size-5" />
    </button>
  );
};

interface PageItemProps {
  item: PaginationItem;
  isCurrent: boolean;
  onClick: (page: number) => void;
}

const PageItem = ({ item, isCurrent, onClick }: PageItemProps) => {
  if (item === PAGINATION_ELLIPSIS) {
    return (
      <span
        data-testid="middle-points"
        aria-hidden="true"
        className={cn(
          BASE_BUTTON_CLASS,
          "bg-ib-white text-ib-black border-ib-gray-borders",
        )}
      >
        <Icon name="ellipsis" className="size-5" />
      </span>
    );
  }

  return (
    <button
      type="button"
      aria-label={`Ir a la página ${item}`}
      aria-current={isCurrent ? "page" : undefined}
      className={cn(BASE_BUTTON_CLASS, PAGE_BUTTON_CLASS)}
      disabled={isCurrent}
      onClick={() => onClick(item)}
    >
      {item}
    </button>
  );
};

const Pagination = ({
  lastPage,
  currentPage,
  changePage,
  limit,
  setLimit,
}: PaginationProps) => {
  const mobile = isMobile();

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > lastPage || page === currentPage) return;
      changePage(page - 1);
    },
    [changePage, currentPage, lastPage],
  );

  const items = useMemo(
    () =>
      buildPaginationItems({
        currentPage,
        lastPage,
        siblingCount: 1,
        boundaryCount: 1,
        compact: mobile,
      }),
    [currentPage, lastPage, mobile],
  );

  if (lastPage <= 1) return null;

  return (
    <nav
      aria-label="Paginación"
      data-testid="table-pagination"
      className={cn("w-full flex justify-between pb-10")}
    >
      <div
        className={cn(
          "flex flex-row items-center gap-x-1 w-full md:w-fit",
          lastPage > 3 ? "max-md:justify-between" : "max-md:justify-center",
        )}
      >
        {!mobile && lastPage > 3 && (
          <NavButton
            testId="first-button"
            icon="doubleChevronLeft"
            ariaLabel="Ir a la primera página"
            disabled={currentPage === 1}
            onClick={() => goToPage(1)}
          />
        )}

        <NavButton
          testId="previous-button"
          icon="chevronLeft"
          ariaLabel="Ir a la página anterior"
          disabled={currentPage === 1}
          onClick={() => goToPage(currentPage - 1)}
        />

        {items.map((item, index) => {
          const key =
            item === PAGINATION_ELLIPSIS ? `ellipsis-${index}` : `page-${item}`;

          return (
            <PageItem
              key={key}
              item={item}
              isCurrent={item === currentPage}
              onClick={goToPage}
            />
          );
        })}

        <NavButton
          testId="next-button"
          icon="chevronRight"
          ariaLabel="Ir a la página siguiente"
          disabled={currentPage === lastPage}
          onClick={() => goToPage(currentPage + 1)}
        />

        {!mobile && lastPage > 3 && (
          <NavButton
            testId="last-button"
            icon="doubleChevronRight"
            ariaLabel="Ir a la última página"
            disabled={currentPage === lastPage}
            onClick={() => goToPage(lastPage)}
          />
        )}
      </div>
      {!mobile && setLimit && limit && (
        <Limit limit={limit} setLimit={setLimit} />
      )}
    </nav>
  );
};

export default memo(Pagination);
